import React, { useRef, useState } from 'react';

import { AutocompleteInputChangeReason } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWRInfinite from 'swr/infinite';

import { AsyncAutocomplete, AsyncAutocompleteProps } from '~components';
import { OffsetPagedResults } from '~interfaces';
import i18n from '~lib/i18n';

import Organisation from '../../interfaces/organisation';
import OrganisationsService from '../../services/organisationsService';

const service = new OrganisationsService();

interface OrganisationAutocompleteProps
	extends Omit<AsyncAutocompleteProps<Organisation>, 'options' | 'label'> {
	label?: string;
	count?: number;
}

const OrganisationAutocomplete = ({
	label = i18n.t('organisation'),
	count = 10,
	value,
	...asyncAutocompleteProps
}: OrganisationAutocompleteProps) => {
	const { t } = useTranslation();

	const [searchQuery, setSearchQuery] = useState('');
	const [open, setOpen] = useState(false);

	// Let's set the on the first page response. Accept that it may happen
	// if an organisation is added in the mean time
	// Small changes right..?
	const total = useRef<number>(undefined);
	const page = useRef(1);

	const getKey = (i: number, previousData: OffsetPagedResults<Organisation[]>) => {
		if (!open || (previousData && !previousData.hasMore)) {
			// Not open or reached the end
			return null;
		}

		page.current = i + 1;

		return [
			service.basePath,
			{
				page: page.current,
				pageSize: count,
				searchQuery: searchQuery || undefined,
			},
		] as const;
	};

	const { data, isLoading, isValidating, size, setSize } = useSWRInfinite(
		getKey,
		([_, args]) => service.getOrganisations(args),
		{
			keepPreviousData: true,
			revalidateFirstPage: false,
			onSuccess: (res) => (total.current = res[0].total),
		}
	);

	/**
	 * Add a page on a overflow (scroll at the bottom of the list)
	 */
	const handleOverflow = () => {
		if (total.current != null && (size - 1) * count <= total.current) {
			setSize(size + 1);
		}
	};

	const handleInputChange = (
		e: React.SyntheticEvent<Element, Event>,
		value: string,
		reason: AutocompleteInputChangeReason
	) => {
		setSearchQuery(value);

		asyncAutocompleteProps.onInputChange?.(e, value, reason);
	};

	return (
		<AsyncAutocomplete
			{...asyncAutocompleteProps}
			value={value}
			label={label}
			loading={isLoading || isValidating}
			noOptionsText={t('noResults')}
			onOpen={() => setOpen(true)}
			onClose={() => setOpen(false)}
			options={data?.flatMap((el) => el.results) ?? []}
			onInputChange={handleInputChange}
			onOverflow={handleOverflow}
		/>
	);
};

export default OrganisationAutocomplete;
